/**
 * Firestore — ask mode sessions (conversations collection, default DB).
 * Track C2.
 *
 * Collection: conversations
 * Database:   (default) — use getFirebaseFirestore()
 *
 * RULES: Never write undefined/null to Firestore.
 *        Always run removeUndefined() on message arrays before writes.
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import { tokenLimitForPlan } from "@/shared/types";
import type { Session, Message } from "@/modes/ask/types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Recursively strips undefined and null values from any object/array.
 * Firestore rejects writes that contain undefined fields.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) {
    return obj
      .map(removeUndefined)
      .filter((item) => item !== null && item !== undefined);
  }
  if (typeof obj === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeUndefined(value);
      if (cleanedValue !== undefined && cleanedValue !== null) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }
  return obj;
}

function cleanMessage(msg: Message): Record<string, unknown> {
  return removeUndefined({
    id:         msg.id ?? "",
    role:       msg.role ?? "user",
    content:    msg.content ?? "",
    timestamp:  msg.timestamp ?? new Date().toISOString(),
    attachments: msg.attachments ?? [],
    suggestions: msg.suggestions ?? [],
    token_usage: msg.token_usage
      ? {
          input_tokens:  msg.token_usage.input_tokens  ?? 0,
          output_tokens: msg.token_usage.output_tokens ?? 0,
          total_tokens:  msg.token_usage.total_tokens  ?? 0,
        }
      : undefined,
  });
}

// ─── Token limit ──────────────────────────────────────────────────────────────

// Centralized in @/shared/types — tokenLimitForPlan() keeps the paid ceiling
// (PAID_TOKEN_LIMIT) and free ceiling (FREE_TOKEN_LIMIT) in one place.

// ─── CRUD ─────────────────────────────────────────────────────────────────────

/**
 * Create a new conversation document and return the full Session.
 * Mirrors React's createConversation() in lib/userProfile.ts.
 */
export async function createSession(
  userId: string,
  title: string,
  plan: string | undefined,
  firstMessage: Message
): Promise<Session> {
  const db = getFirebaseFirestore();
  const tokensLimit = tokenLimitForPlan(plan);

  const data: Omit<Session, "id"> = {
    userId,
    title,
    lastMessage: "",
    tokensUsed:  0,
    tokensLimit,
    messages:    [cleanMessage(firstMessage) as unknown as Message],
    createdAt:   new Date().toISOString(),
    updatedAt:   new Date().toISOString(),
  };

  const ref = await addDoc(collection(db, "conversations"), data);
  return { id: ref.id, ...data };
}

/** Fetch a single conversation by ID. Returns null if not found. */
export async function getSession(sessionId: string): Promise<Session | null> {
  try {
    const db = getFirebaseFirestore();
    const snap = await getDoc(doc(db, "conversations", sessionId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Session;
  } catch (err) {
    throw withContext("ask.getSession", err);
  }
}

/**
 * How many conversations a single sidebar read may pull.
 *
 * Every conversation document embeds its whole `messages` array, so the cap is
 * really a PAYLOAD cap: Firestore rejects a response that exceeds ~10 MiB, and
 * a rejected query fails in full — an over-large cap therefore does not return
 * a short list, it returns NOTHING and the sidebar comes up empty. Accounts
 * with long assistant answers hit that quickly, so the ladder below backs off
 * instead of failing: a heavy user sees fewer conversations rather than none.
 */
const SESSION_PAGE_CAPS = [50, 25, 10];

/**
 * Fetch all conversations for a user, ordered by most-recently-updated.
 * Mirrors React's getUserConversations().
 *
 * Walks SESSION_PAGE_CAPS from the widest down, returning the first read that
 * succeeds. Throws only when every cap fails.
 */
export async function getSessions(userId: string): Promise<Session[]> {
  const db = getFirebaseFirestore();
  let lastErr: unknown = null;

  for (const cap of SESSION_PAGE_CAPS) {
    try {
      const q = query(
        collection(db, "conversations"),
        where("userId", "==", userId),
        orderBy("updatedAt", "desc"),
        limit(cap)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }) as Session)
        .filter((s) => !s.isDeleted);
    } catch (err) {
      lastErr = err;
      console.warn(
        `[ask] getSessions read failed at cap=${cap}, backing off`,
        err instanceof Error ? err.message : err
      );
    }
  }

  throw withContext("ask.getSessions", lastErr);
}

/**
 * Update conversation fields.
 * Runs removeUndefined on any messages array before writing.
 */
export async function updateSession(
  sessionId: string,
  updates: Partial<Session>
): Promise<void> {
  const db = getFirebaseFirestore();
  const ref = doc(db, "conversations", sessionId);

  // Clean messages if included in the update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: Record<string, any> = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  if (payload.messages) {
    payload.messages = payload.messages.map(cleanMessage);
  }

  await updateDoc(ref, removeUndefined(payload));
}

/**
 * Delete a conversation (soft delete).
 * Free users' sessions always count toward the 5-session limit, even when
 * "deleted" — otherwise they could delete chats and bypass the cap.
 */
export async function deleteSession(sessionId: string, isPro: boolean = false): Promise<void> {
  const db = getFirebaseFirestore();
  const ref = doc(db, "conversations", sessionId);
  if (isPro) {
    await deleteDoc(ref);
  } else {
    await updateDoc(ref, {
      isDeleted: true,
      updatedAt: new Date().toISOString(),
    });
  }
}

// ─── Session Limits ─────────────────────────────────────────────────────────

const FREE_SESSIONS_LIMIT = 5;

export interface SessionLimitCheck {
  allowed: boolean;
  sessionsUsed: number;
  sessionsLimit: number;
}

export async function getSessionCount(userId: string): Promise<number> {
  const db = getFirebaseFirestore();
  const q = query(
    collection(db, "conversations"),
    where("userId", "==", userId),
    // Bound the fetch — a session-count > FREE_SESSIONS_LIMIT is denied
    // regardless of the exact number, so a cap is safe and keeps reads tiny.
    limit(26),
  );
  // Plain getDocs instead of getCountFromServer: the aggregation query needs
  // list/count permissions that can fail with "Missing or insufficient
  // permissions" for free users (and is unsupported in the emulator).
  const snap = await getDocs(q);
  return snap.size;
}

function withContext(op: string, err: unknown): Error {
  const msg = err instanceof Error ? err.message : String(err);
  return new Error(`${msg} [${op}]`);
}

export async function canCreateSession(userId: string): Promise<SessionLimitCheck> {
  const count = await getSessionCount(userId);

  if (count < FREE_SESSIONS_LIMIT) {
    return { allowed: true, sessionsUsed: count, sessionsLimit: FREE_SESSIONS_LIMIT };
  }

  return { allowed: false, sessionsUsed: count, sessionsLimit: FREE_SESSIONS_LIMIT };
}
